<script setup>
import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  isCollapsed: { type: Boolean, default: false },
  isQaMonitor: { type: Boolean, default: false },
  hideFilterControls: { type: Boolean, default: false },
  hideCollapseToggle: { type: Boolean, default: false },
  activeAssigneeTab: { type: String, default: '' },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'toggleCollapse',
  'clearAllUnread',
]);

const isUnreadTab = computed(() => props.activeAssigneeTab === 'unread');

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="wlab-chat-list-header flex items-center justify-between gap-2 px-4"
  >
    <div class="flex items-center justify-center min-w-0 gap-2">
      <button
        v-if="!isQaMonitor && !hideCollapseToggle"
        v-tooltip.right="
          isCollapsed
            ? $t('CHAT_LIST.EXPAND_SIDEBAR')
            : $t('CHAT_LIST.COLLAPSE_SIDEBAR')
        "
        class="wlab-chat-list-header__collapse flex items-center justify-center w-8 h-8 rounded hover:bg-n-slate-3 transition-colors shrink-0"
        @click="emit('toggleCollapse')"
      >
        <span
          class="text-lg"
          :class="
            isCollapsed
              ? 'i-lucide-panel-left-open'
              : 'i-lucide-panel-left-close'
          "
        />
      </button>
      <h1
        v-if="!isCollapsed"
        class="text-base font-medium truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="
          !isCollapsed &&
          allCount > 0 &&
          hasAppliedFiltersOrActiveFolders &&
          !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <!-- Status badge removed -->
    </div>
    <div v-if="!isCollapsed && !isQaMonitor" class="flex items-center gap-1">
      <NextButton
        v-if="isUnreadTab"
        v-tooltip.top-end="$t('CHAT_LIST.CLEAR_ALL_UNREAD')"
        icon="i-lucide-check-check"
        slate
        xs
        faded
        @click="emit('clearAllUnread')"
      />
      <template
        v-if="!hideFilterControls && hasAppliedFilters && !hasActiveFolders"
      >
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="!hideFilterControls && hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else-if="!hideFilterControls" class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>
      <ConversationBasicFilter
        v-if="!hideFilterControls && !hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>

<style scoped>
.wlab-chat-list-header {
  height: 56px;
  min-height: 56px;
  flex-shrink: 0;
  background-color: var(--wlab-cl-header-bg);
  border-bottom: 1px solid var(--wlab-cl-header-border);
  font-family: 'Excon', sans-serif;
}

.wlab-chat-list-header h1 {
  font-size: 16px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
  color: var(--wlab-cl-title-color) !important;
  font-family: 'Excon', sans-serif !important;
}

.wlab-chat-list-header span {
  font-size: 10px !important;
  font-family: 'Excon', sans-serif !important;
}

/* header action button style */
:deep(.wlab-chat-list-header button) {
  border-radius: 8px !important;
}
@media (max-width: 768px) {
  .wlab-chat-list-header__collapse {
    display: none !important;
  }
}
</style>
